import Button from "@components/Button";
import { useCallback, useMemo } from "react";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { useKeyboardShortcut } from "@/hooks/useKeyboardShortcut";
import { useImproveContext } from "@/contexts/ImproveContext";
import {
    useLimitedSources,
    useApprovedImageIds,
    useCurrentMedia,
    useApprovedAnnotationSnapshot,
    useAnnotationsForFile
} from "@/store";

export const ApproveImageBtn = ({ disabled = false }: { disabled?: boolean }) => {
    const { track } = useAnalytics();
    const { handleApproveImage } = useImproveContext();
    const sources = useLimitedSources();
    const approvedImageIds = useApprovedImageIds();
    const currentMedia = useCurrentMedia();

    // Check if the current image has already been approved
    const currentImageId = currentMedia?.file?.id;
    const isCurrentImageApproved = currentImageId ? approvedImageIds.has(currentImageId) : false;

    // Get the stored annotation snapshot for the current image (if approved)
    const approvedSnapshot = useApprovedAnnotationSnapshot(currentImageId);

    // Get current annotations for the current image
    const annotationsForFile = useAnnotationsForFile(currentImageId);

    // Check if annotations have changed since approval
    const hasAnnotationChanges = useMemo(() => {
        // If not approved yet, no need to check for changes
        if (!isCurrentImageApproved || !approvedSnapshot || !currentImageId) {
            return true; // Enable button for unapproved images
        }

        // Get current annotations (for images, frameId = fileId)
        const currentAnnotations = annotationsForFile[currentImageId] || [];

        // Create current snapshot in same format as stored snapshot
        const currentSnapshot = JSON.stringify(
            currentAnnotations.map((a) => ({
                x: a.x,
                y: a.y,
                width: a.width,
                height: a.height,
                label: a.label
            }))
        );

        // Compare snapshots
        return currentSnapshot !== approvedSnapshot;
    }, [isCurrentImageApproved, approvedSnapshot, currentImageId, annotationsForFile]);

    // Disable if no sources, or if already approved with no changes
    const isDisabled =
        disabled || sources.length === 0 || (isCurrentImageApproved && !hasAnnotationChanges);

    const onClickApprove = useCallback(() => {
        track({
            event: AnalyticsFlowEvent.IMPROVE_IMAGE_APPROVED,
            data: { isUpdate: isCurrentImageApproved }
        });
        handleApproveImage();
    }, [handleApproveImage, track, isCurrentImageApproved]);

    // Keyboard shortcut: Command + Enter
    useKeyboardShortcut({
        key: "Enter",
        handler: onClickApprove,
        disabled: isDisabled,
        requireMeta: true
    });

    // Change button text based on whether the image is already approved
    const buttonText = isCurrentImageApproved ? "Save Changes" : "Approve Image";
    const buttonIcon = isCurrentImageApproved ? "far fa-save" : "far fa-check";

    return (
        <div className="flex flex-col gap-4">
            <Button onClick={onClickApprove} disabled={isDisabled} className="w-full">
                <i className={buttonIcon}></i>
                <span>{buttonText}</span>
                <div className={twClassNames("hotkeysContainer", isDisabled && "opacity-30")}>
                    <span className="hotkey dark-brand">⌘</span>
                    <span className="hotkey dark-brand">⏎</span>
                </div>
            </Button>
        </div>
    );
};
